import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * V8.7.5 — Avisos do Gerenciador (api/avisos.php), mostrados depois de escolher o perfil.
 * Usado pelo Player Web (src/App.tsx) e pelo Stick (tvbox-stick/src/App.tsx, via @shared).
 * Cada app passa as funções da sua API. No Stick funciona com o controle remoto: ← → troca o botão,
 * OK confirma e Voltar = "Lembrar depois" (ou OK, se o aviso não tiver lembrete).
 *
 * V8.7.6: consulta os avisos UMA vez por entrada no perfil. Avisos "imediatos" também chegam na hora,
 * mas nunca por cima de um vídeo (ativo = false enquanto toca):
 *  - Stick: o app nativo já fala com o servidor a cada 1 min e dispara o evento 'opentv:avisos' com a marca.
 *  - Web: pergunta só a marca (um número) a cada 5 min, com a aba aberta e sem vídeo.
 * Só quando a marca muda é que busca os avisos (só os imediatos).
 */

export type Aviso = {
  id: number;
  titulo?: string | null;
  texto?: string | null;
  imagem?: string | null;
  botao?: string | null;
  pode_lembrar?: boolean;
};

interface Props {
  /** Só busca quando a pessoa já entrou e escolheu o perfil. */
  ativo: boolean;
  profileId: string | null;
  plataforma: 'stick' | 'web';
  buscar: (profileId: string, plataforma: 'stick' | 'web', soImediatos?: boolean) => Promise<{ avisos: Aviso[]; marca?: number }>;
  /** Web: pergunta a marca dos avisos imediatos (leve). No Stick não precisa (vem pelo evento do app nativo). */
  buscarMarca?: (plataforma: 'stick' | 'web') => Promise<{ marca?: number }>;
  marcar: (avisoId: number, profileId: string, acao: 'ok' | 'lembrar', plataforma: 'stick' | 'web') => Promise<any>;
  /** Avisa o app quando há aviso na tela (o Stick desliga a navegação do catálogo enquanto isso). */
  aoMudarAberto?: (aberto: boolean) => void;
}


const MARCA_WEB_MS = 5 * 60 * 1000;

export const AvisosTela: React.FC<Props> = ({ ativo, profileId, plataforma, buscar, buscarMarca, marcar, aoMudarAberto }) => {
  const [fila, setFila] = useState<Aviso[]>([]);
  const [focado, setFocado] = useState<0 | 1>(0);
  const okRef = useRef<HTMLButtonElement>(null);
  const lembrarRef = useRef<HTMLButtonElement>(null);
  const atual = ativo ? fila[0] || null : null;

  // V8.7.6: buscar/marcar chegam como funções novas a cada render do App. Guardadas em ref para
  // o efeito abaixo NÃO reiniciar (antes ele zerava a fila e os timers a cada render da tela).
  const buscarRef = useRef(buscar);
  const marcarRef = useRef(marcar);
  buscarRef.current = buscar;
  marcarRef.current = marcar;

  const buscarMarcaRef = useRef(buscarMarca);
  buscarMarcaRef.current = buscarMarca;
  const ativoRef = useRef(ativo);
  ativoRef.current = ativo;
  const profileRef = useRef(profileId);
  profileRef.current = profileId;

  /** Marca dos avisos imediatos já tratada e se chegou marca nova enquanto a tela estava ocupada. */
  const marcaRef = useRef(0);
  const marcaPendenteRef = useRef(0);

  const juntar = (novos: Aviso[]) => setFila((f) => {
    const ids = new Set(f.map((a) => a.id));
    return [...f, ...novos.filter((a) => !ids.has(a.id))];
  });

  // 1) Entrada no perfil: consulta UMA vez. Se começar a tocar algo antes de responder, o aviso fica
  //    guardado e volta ao sair do player (sem consultar de novo).
  const buscouParaRef = useRef<string | null>(null);
  useEffect(() => {
    if (!profileId) { buscouParaRef.current = null; setFila([]); return; }
    if (!ativo || buscouParaRef.current === profileId) return;
    let vivo = true;
    const t = window.setTimeout(async () => {
      try {
        const r = await buscarRef.current(profileId, plataforma);
        if (!vivo) return;
        buscouParaRef.current = profileId;
        if (typeof r?.marca === 'number') marcaRef.current = Math.max(marcaRef.current, r.marca);
        setFila(Array.isArray(r?.avisos) ? r.avisos : []);
      } catch { /* sem internet: tenta de novo na próxima vez que a tela ficar livre */ }
    }, 2500); // deixa a tela principal aparecer antes
    return () => { vivo = false; window.clearTimeout(t); };
  }, [ativo, profileId, plataforma]);

  // 2) Avisos imediatos: busca só quando a marca muda e a tela está livre
  const buscandoImediatosRef = useRef(false);
  const buscarImediatos = useCallback(async (marca: number) => {
    const pid = profileRef.current;
    if (!pid || buscouParaRef.current !== pid) return; // a entrada no perfil ainda vai buscar tudo
    if (marca <= marcaRef.current) return;
    if (!ativoRef.current) { marcaPendenteRef.current = Math.max(marcaPendenteRef.current, marca); return; }
    if (buscandoImediatosRef.current) return;
    buscandoImediatosRef.current = true;
    try {
      const r = await buscarRef.current(pid, plataforma, true);
      marcaRef.current = Math.max(marca, typeof r?.marca === 'number' ? r.marca : 0);
      marcaPendenteRef.current = 0;
      if (profileRef.current === pid && Array.isArray(r?.avisos) && r.avisos.length) juntar(r.avisos);
    } catch { /* tenta na próxima marca */ } finally {
      buscandoImediatosRef.current = false;
    }
  }, [plataforma]);

  // Saiu do vídeo / fechou a janela de perfis e tinha marca nova esperando
  useEffect(() => {
    if (ativo && marcaPendenteRef.current > marcaRef.current) buscarImediatos(marcaPendenteRef.current);
  }, [ativo, buscarImediatos]);

  // Stick: evento disparado pelo app nativo (MonitorStick, a cada 1 min, só quando muda)
  useEffect(() => {
    const onEvento = (e: Event) => {
      const m = Number((e as CustomEvent).detail) || 0;
      if (m > 0) buscarImediatos(m);
    };
    window.addEventListener('opentv:avisos', onEvento);
    return () => window.removeEventListener('opentv:avisos', onEvento);
  }, [buscarImediatos]);

  // Web: pergunta a marca a cada 5 min, só com a aba aberta e a tela livre
  useEffect(() => {
    if (!buscarMarca) return;
    const t = window.setInterval(async () => {
      if (!ativoRef.current || !profileRef.current || document.visibilityState !== 'visible') return;
      try {
        const r = await buscarMarcaRef.current?.(plataforma);
        const m = Number(r?.marca) || 0;
        if (m > 0) buscarImediatos(m);
      } catch { /* sem internet */ }
    }, MARCA_WEB_MS);
    return () => window.clearInterval(t);
  }, [!!buscarMarca, plataforma, buscarImediatos]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { aoMudarAberto?.(!!atual); }, [atual, aoMudarAberto]);

  useEffect(() => {
    if (!atual) return;
    setFocado(0);
    const t = window.setTimeout(() => okRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, [atual]);

  const responder = useCallback((acao: 'ok' | 'lembrar') => {
    if (!atual || !profileId) return;
    marcarRef.current(atual.id, profileId, acao, plataforma).catch(() => {});
    setFila((f) => f.slice(1));
  }, [atual, profileId, plataforma]);

  // Controle remoto / teclado: só o aviso responde enquanto ele está na tela
  useEffect(() => {
    if (!atual) return;
    const onKey = (e: KeyboardEvent) => {
      const k = e.key;
      if (k === 'ArrowLeft' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowDown') {
        if (atual.pode_lembrar) {
          const novo: 0 | 1 = focado === 0 ? 1 : 0;
          setFocado(novo);
          (novo === 0 ? okRef : lembrarRef).current?.focus();
        }
      } else if (k === 'Enter' || k === ' ') {
        responder(focado === 1 && atual.pode_lembrar ? 'lembrar' : 'ok');
      } else if (k === 'Escape' || k === 'Backspace' || k === 'GoBack' || k === 'BrowserBack') {
        responder(atual.pode_lembrar ? 'lembrar' : 'ok');
      } else {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      (e as any).stopImmediatePropagation?.();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [atual, focado, responder]);

  if (!atual) return null;

  // V8.7.6: estilos inline (o Stick não gera as classes do Tailwind de arquivos fora da pasta dele)
  const botao = (foco: boolean): React.CSSProperties => ({
    flex: '0 0 auto', whiteSpace: 'nowrap', minWidth: 140, padding: '12px 28px', borderRadius: 12,
    fontSize: 18, fontWeight: 800, lineHeight: 1.2, cursor: 'pointer', outline: 'none',
    border: foco ? '2px solid #fbbf24' : '2px solid #3f3f46',
    background: foco ? '#f59e0b' : '#27272a', color: foco ? '#000' : '#fff',
    boxShadow: foco ? '0 0 0 4px rgba(245,158,11,0.25)' : 'none',
  });
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.82)' }}>
      <div style={{
        width: 'min(760px, 92vw)', maxHeight: '90vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 16,
        background: '#18181b', border: '1px solid #3f3f46', borderRadius: 18, padding: '28px 32px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', fontFamily: 'inherit',
      }}>
        {atual.titulo && (
          <div style={{ color: '#fff', fontSize: 28, fontWeight: 900, lineHeight: 1.2, overflowWrap: 'anywhere' }}>{atual.titulo}</div>
        )}
        {atual.imagem && (
          <img src={atual.imagem} alt="" style={{ display: 'block', width: '100%', maxHeight: '42vh', objectFit: 'contain', borderRadius: 10, flex: '0 1 auto', minHeight: 0 }} />
        )}
        {atual.texto && (
          <div style={{ color: '#e4e4e7', fontSize: 20, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', overflowY: 'auto', minHeight: 0, flex: '0 1 auto' }}>
            {atual.texto}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingTop: 4, flex: '0 0 auto' }}>
          {atual.pode_lembrar && (
            <button ref={lembrarRef} onClick={() => responder('lembrar')} onFocus={() => setFocado(1)} style={botao(focado === 1)}>
              Lembrar depois
            </button>
          )}
          <button ref={okRef} onClick={() => responder('ok')} onFocus={() => setFocado(0)} style={botao(focado === 0)}>
            {atual.botao || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};
